export type TriggerEvent = "callStart" | "duringCall" | "callEnd" | "callStatusWebhook";
export type SectionId = "when" | "decide" | "request" | "hears" | "save" | "advanced";

export type Section = {
  id: SectionId;
  number: number | null;
  title: string;
  summary: string;
};

export const MOMENT_OPTIONS: Array<{ value: TriggerEvent; label: string; description: string }> = [
  { value: "callStart", label: "Start of the call", description: "Before the assistant speaks" },
  { value: "duringCall", label: "During the call", description: "When the conversation calls for it" },
  { value: "callEnd", label: "End of the call", description: "Once the call has finished" },
  { value: "callStatusWebhook", label: "Call status changes", description: "Answered, missed, ended…" },
];

const KNOWN_EVENTS: TriggerEvent[] = ["callStart", "duringCall", "callEnd", "callStatusWebhook"];

function normalize(triggerEvent: string | undefined | null): TriggerEvent {
  return KNOWN_EVENTS.includes(triggerEvent as TriggerEvent) ? (triggerEvent as TriggerEvent) : "duringCall";
}

export function showsCallOnce(triggerEvent: string): boolean {
  const event = normalize(triggerEvent);
  return event === "duringCall" || event === "callStatusWebhook";
}

export function showsCallStatusValues(triggerEvent: string): boolean {
  return normalize(triggerEvent) === "callStatusWebhook";
}

export function showsSequencing(triggerEvent: string): boolean {
  return normalize(triggerEvent) === "duringCall";
}

export function showsSpokenMessages(triggerEvent: string): boolean {
  return normalize(triggerEvent) === "duringCall";
}

export type DecideCopy = {
  title: string;
  summary: string;
  showsQuickCheck: boolean;
  detailedLabel: string;
  detailedDescription: string;
  conversationLabel: string;
};

export function decideCopy(triggerEvent: string): DecideCopy | null {
  switch (normalize(triggerEvent)) {
    case "duringCall":
      return {
        title: "How do we decide to run it?",
        summary: "Shown because this API runs during the call. The assistant reads these instructions against the conversation so far.",
        showsQuickCheck: true,
        detailedLabel: "Detailed check",
        detailedDescription:
          "Read only when the quick check passes, or on every turn if there is no quick check. Put large reference material here, such as a full menu, and refer to call variables with curly brackets.",
        conversationLabel: "Pulled out by the detailed check",
      };
    case "callEnd":
      return {
        title: "What to pull from the conversation",
        summary:
          "Shown because this API runs at the end of the call. Once the call has finished, the assistant reads the whole conversation against these instructions and pulls out the values your request fields need.",
        showsQuickCheck: false,
        detailedLabel: "Instructions",
        detailedDescription:
          "Describe what to take from the conversation and name each value exactly like the request field it fills.",
        conversationLabel: "Pulled out of the conversation",
      };
    default:
      return null;
  }
}

export function advancedSummary(triggerEvent: string): string {
  return showsSequencing(triggerEvent)
    ? "Context filter · Then run other APIs · Available only after another API"
    : "Context filter";
}

export function sectionsFor(triggerEvent: string): Section[] {
  const event = normalize(triggerEvent);
  const decide = decideCopy(event);

  const numbered: Array<Omit<Section, "number">> = [
    { id: "when", title: "When does it run?", summary: "The moment decides which of the sections below apply." },
  ];
  if (decide) numbered.push({ id: "decide", title: decide.title, summary: decide.summary });
  numbered.push({ id: "request", title: "The request", summary: "Where to send it, and what to include." });
  if (showsSpokenMessages(event)) {
    numbered.push({
      id: "hears",
      title: "What the caller hears",
      summary:
        "Shown because this API runs during the call. The assistant says these in the caller's language. Leave one empty and the assistant carries on naturally instead.",
    });
  }
  numbered.push({
    id: "save",
    title: "Save from the response",
    summary: "Keep parts of the reply so later requests or the assistant can use them.",
  });

  const sections: Section[] = numbered.map((section, index) => ({ ...section, number: index + 1 }));
  sections.push({ id: "advanced", number: null, title: "Advanced", summary: advancedSummary(event) });
  return sections;
}
